import { client } from "./supabase";
import { esEmailValido } from "./validarEmail";

// Contactos por lado para el "Escribir" del Radar Comercial (Fase B) —
// docs/decisiones/2026-09-26-radar-mail-pagina-diseno.md §3.2.
//
// Fuente ÚNICA: las tablas relacionales `clientes_contactos_cliente`/`_agencia` (cliente_id,
// usuario_id), NUNCA las columnas legacy `contacto_X_N` de `clientes` — es la misma fuente que ya
// usa el resumen precalculado de actividad (y `radarMailSaliente.js`) para atribuir mail saliente,
// así el destinatario que ve el Radar es el mismo que el sync atribuye a esa empresa.
//
// Batcheado por `clientId` (no una query por fila del panel): el panel solo necesita contactos de
// los `clientId` de las alertas visibles (pendiente+vencida), nunca de las ~400 empresas — un
// fetch único cuando se cargan las alertas, igual que `radarMailSaliente.js`.
//
// `cargo` sale de `usuarios.cargo`, NO de la columna `cargo` de las tablas relacionales: esa
// columna guarda un texto hardcodeado por POSICIÓN (Director/Supervisor/... o Gerente de
// Marketing/Jefe de Medios/...) que el CRM reescribe en cada guardado sin relación con la persona
// real (verificado con datos reales: el mismo texto se repite idéntico en contactos distintos) —
// hallazgo de Cowork documentado en el diseño §9. `usuarios.cargo` es más flojo (sparse, muchos
// null) pero es el dato real de la persona cuando existe.
//
// Contactos sin email quedan afuera acá mismo (no en el componente) — "el lado no tiene
// contactos con email" se decide con `.length === 0` sobre el resultado, sin filtrar de nuevo.
//
// Contactos CON email pero de formato inválido (ej. "samsung.com", solo el dominio, sin usuario —
// caso real Samsung, 2026-09-27) NO se descartan acá: viajan igual, marcados con `emailValido:
// false` (`src/utils/validarEmail.js`, fuente única), para que `EscribirRadarModal` los muestre en
// rojo y avise — descartarlos en silencio ocultaría el problema de dato en vez de mostrarlo
// (10.177). Nunca se envían como destinatario real (ver `EscribirRadarModal.crearBorrador`).
export const fetchContactosPorLado = async ({ clientIds }) => {
  const ids = [...new Set((clientIds || []).filter((id) => id != null))];
  const vacio = new Map(ids.map((id) => [id, { cliente: [], agencia: [] }]));
  if (ids.length === 0) return vacio;

  const [{ data: ccc }, { data: cca }] = await Promise.all([
    client.from("clientes_contactos_cliente").select("cliente_id, usuario_id").in("cliente_id", ids),
    client.from("clientes_contactos_agencia").select("cliente_id, usuario_id").in("cliente_id", ids),
  ]);

  const usuarioIds = [...new Set([...(ccc || []), ...(cca || [])].map((r) => r.usuario_id))];
  if (usuarioIds.length === 0) return vacio;

  const { data: usuarios } = await client
    .from("usuarios")
    .select("id, nombre, apellido, email, cargo")
    .in("id", usuarioIds);
  const usuarioPorId = new Map((usuarios || []).map((u) => [u.id, u]));

  const armar = (rows) =>
    (rows || [])
      .map((r) => usuarioPorId.get(r.usuario_id))
      .filter((u) => u && u.email)
      .map((u) => ({
        usuarioId: u.id,
        nombre: (u.nombre || "").trim(),
        apellido: (u.apellido || "").trim(),
        email: u.email,
        emailValido: esEmailValido(u.email),
        cargo: (u.cargo || "").trim(),
      }));

  const cccPorCliente = new Map();
  (ccc || []).forEach((r) => {
    if (!cccPorCliente.has(r.cliente_id)) cccPorCliente.set(r.cliente_id, []);
    cccPorCliente.get(r.cliente_id).push(r);
  });
  const ccaPorCliente = new Map();
  (cca || []).forEach((r) => {
    if (!ccaPorCliente.has(r.cliente_id)) ccaPorCliente.set(r.cliente_id, []);
    ccaPorCliente.get(r.cliente_id).push(r);
  });

  const resultado = new Map();
  ids.forEach((id) => {
    resultado.set(id, {
      cliente: armar(cccPorCliente.get(id)),
      agencia: armar(ccaPorCliente.get(id)),
    });
  });
  return resultado;
};
